import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin, adminStats, adminRecent } from "@/lib/api";
import { toast } from "sonner";

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-white/10 p-6" data-testid={testid}>
      <div
        className="font-ui text-white/40"
        style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}
      >
        {label}
      </div>
      <div className="mt-3 font-mono-void text-white" style={{ fontSize: "28px" }}>
        {value}
      </div>
    </div>
  );
}

function AdminLoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(email, password);
      localStorage.setItem("void_admin_token", token);
      onSuccess();
    } catch {
      toast.error("Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-black px-6">
      <form
        onSubmit={submit}
        className="void-glass w-full max-w-sm px-8 py-10"
        data-testid="admin-login-form"
      >
        <div
          className="font-serif text-white/85 text-center"
          style={{ fontSize: "28px", fontVariationSettings: '"opsz" 144, "SOFT" 0' }}
        >
          The Void
        </div>
        <div
          className="mt-1 font-ui text-white/30 text-center"
          style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}
        >
          Keeper access
        </div>

        <div className="mt-10 space-y-6">
          <div>
            <label
              className="block font-ui text-white/40 mb-2"
              style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}
            >
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-white/60 outline-none py-2 font-mono-void text-white"
              data-testid="admin-email"
              required
            />
          </div>
          <div>
            <label
              className="block font-ui text-white/40 mb-2"
              style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}
            >
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-white/60 outline-none py-2 font-mono-void text-white"
              data-testid="admin-password"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="void-cta w-full mt-10"
          data-testid="admin-submit"
        >
          {loading ? "…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("void_admin_token"));
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [s, r] = await Promise.all([adminStats(), adminRecent()]);
      setStats(s);
      setRecent(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("void_admin_token");
        setAuthed(false);
      }
    }
  };

  useEffect(() => {
    if (authed) {
      load();
      const id = setInterval(load, 15000);
      return () => clearInterval(id);
    }
  }, [authed]);

  if (!authed) return <AdminLoginForm onSuccess={() => setAuthed(true)} />;

  if (!stats)
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white/40">
        loading…
      </div>
    );

  const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(stats.revenue_usd || 0);
  const pct = ((stats.counter / stats.target) * 100).toFixed(2);

  return (
    <div className="min-h-screen bg-black text-white p-6 sm:p-10" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-white/10 pb-6">
        <div>
          <div className="font-serif text-white/80" style={{ fontSize: "22px", fontVariationSettings: '"opsz" 144, "SOFT" 0' }}>
            The Void · Keeper
          </div>
          <div className="font-ui text-white/30" style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}>
            Private dashboard
          </div>
        </div>
        <div className="flex items-center gap-8">
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.origin);
            }}
            className="font-ui text-white/40 hover:text-white/80"
            style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}
            data-testid="copy-origin"
          >
            Copy link
          </button>
          <button
            onClick={() => {
              localStorage.removeItem("void_admin_token");
              navigate("/");
            }}
            className="font-ui text-white/40 hover:text-white/80"
            style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}
            data-testid="admin-logout"
          >
            Sign out
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <StatCard label="Counter" value={new Intl.NumberFormat("en-US").format(stats.counter)} testid="stat-counter" />
        <StatCard label="Toward 1M" value={`${pct}%`} testid="stat-pct" />
        <StatCard label="Revenue" value={money} testid="stat-revenue" />
        <StatCard label="Paid txns" value={new Intl.NumberFormat("en-US").format(stats.paid_payments)} testid="stat-paid" />
        <StatCard label="Attempts" value={new Intl.NumberFormat("en-US").format(stats.total_payment_attempts)} testid="stat-attempts" />
        <StatCard label="Conversion" value={`${(stats.conversion_rate * 100).toFixed(1)}%`} testid="stat-conv" />
        <StatCard label="Today" value={new Intl.NumberFormat("en-US").format(stats.swallowed_today)} testid="stat-today" />
        <StatCard label="Event" value={stats.event_started ? "STARTED" : "waiting"} testid="stat-event" />
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-white/10 p-6" data-testid="by-category">
          <div className="font-ui text-white/40" style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}>
            By category
          </div>
          <div className="mt-4 space-y-2">
            {Object.entries(stats.by_category || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/5 py-2">
                <div className="font-ui text-white/60" style={{ fontSize: "12px", letterSpacing: "0.2em" }}>{k}</div>
                <div className="font-mono-void">{new Intl.NumberFormat("en-US").format(v)}</div>
              </div>
            ))}
            {Object.keys(stats.by_category || {}).length === 0 && (
              <div className="text-white/30 font-ui text-sm">no swallowed messages yet</div>
            )}
          </div>
        </div>

        <div className="border border-white/10 p-6" data-testid="recent">
          <div className="font-ui text-white/40" style={{ fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase" }}>
            Recent activity · metadata only
          </div>
          <div className="mt-4 max-h-96 overflow-auto">
            {recent.length === 0 ? (
              <div className="text-white/30 font-ui text-sm">nothing yet</div>
            ) : recent.map((r) => (
              <div key={r.id} className="flex justify-between border-b border-white/5 py-2">
                <div className="font-mono-void text-white/70">#{r.number}</div>
                <div className="font-ui text-white/40" style={{ fontSize: "11px", letterSpacing: "0.2em" }}>
                  {r.category || "—"}
                </div>
                <div className="font-mono-void text-white/30" style={{ fontSize: "11px" }}>
                  {r.swallowed_at ? new Date(r.swallowed_at).toLocaleString() : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="mt-10 text-white/25 font-ui" style={{ fontSize: "10px", letterSpacing: "0.28em", textTransform: "uppercase" }}>
        Message contents are never shown here.
      </footer>
    </div>
  );
}
